import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useLogoTheme } from '../context/LogoThemeContext';
import { fetchCollection, normalizeSlug } from '../lib/cms';
import { imageUrl } from '../lib/images';

const DEFAULT_OFFERS = [
  {
    id: 'forbundet-fra-hjertet',
    forlob_slug: 'forbundet-fra-hjertet',
    titel: 'Forbundet fra Hjertet',
    undertitel: 'Et trygt og kærligt rum for kvinder, der længes efter at komme hjem til sig selv',
    badge: '5 mødegange · hver anden uge · 2,5 timer • 6 kvinder · 3.500 kr.',
    kort_beskrivelse: 'Forbundet fra Hjertet er et intimt gruppeforløb for kvinder, hvor du får mulighed for at sænke skuldrene, lytte indad og mærke, hvad der virkelig føles sandt for dig. Her skal du ikke præstere, fikse eller være noget bestemt. Du bliver mødt, præcis som du er – med nærvær, omsorg og kærlighed.',
    highlights: 'Kropslige øvelser & åndedræt • Astrologien & energien lige nu • Kroppens sprog (GNM) • Lyt til hjertet & intuition • Kærlig forbindelse til dig selv',
    link: '/forbundet-fra-hjertet',
    billede: ['https://assets.auxodev.dk/assets/40e16184-d312-45e0-99c1-a83e20a53a01/forlob-group.png'],
    cta_tekst: 'Læs mere om Forbundet fra Hjertet'
  },
  {
    id: '1-1-forlob',
    forlob_slug: '1-1-forlob',
    titel: 'En rejse hjem til dig selv',
    undertitel: 'Et personligt 1:1-forløb for kvinder',
    badge: 'Individuelt 1:1-forløb • Skræddersyet til dig',
    kort_beskrivelse: 'I dette 1:1-forløb skaber vi et trygt og fortroligt rum, der tager udgangspunkt i dig. Der er ikke en fast opskrift, vi begynder dér, hvor du er. Sammen undersøger vi det, der fylder, det der længes efter at blive set, og det du måske er klar til at slippe. Jeg holder rummet for dig, mens vi lytter til både kroppen, hjertet og det, der ligger under ordene.',
    highlights: 'Lyt til hjertet & intuition • Kom ned i kroppen • Kroppens sprog & signaler • Slip gamle mønstre • Stå stærkere i dig selv',
    link: '/1-1-forlob',
    billede: ['https://assets.auxodev.dk/assets/40e16184-d312-45e0-99c1-a83e20a53a01/lenea-evolinee-session.webp'],
    cta_tekst: 'Læs mere om 1:1-forløbet'
  },
  {
    id: 'lyt-fra-hjertet',
    forlob_slug: 'lyt-fra-hjertet',
    titel: 'Lyt – fra Hjertet',
    undertitel: 'Et trygt rum, hvor du må dele og bare blive hørt',
    badge: 'Én gang om måneden · Ca. 2 timer • 200 kr. pr. gang',
    kort_beskrivelse: 'Et månedligt rum for kvinder, hvor vi mødes i nærvær og fortrolighed. Et rum, hvor du må komme præcis, som du er. Her behøver du ikke have en færdig historie, du behøver ikke vide, hvad du føler, og du behøver ikke finde løsningen. Du må bare dele det, der er – eller bare lytte.',
    highlights: 'Lille, intim gruppe i klinikken • Ingen dagsorden, vi tager afsæt i det levende • Fortrolighed & respekt • Tilmelding fra gang til gang',
    link: '/lyt-fra-hjertet',
    billede: ['https://assets.auxodev.dk/assets/40e16184-d312-45e0-99c1-a83e20a53a01/img-5756.jpeg'],
    cta_tekst: 'Læs mere om Lyt – fra Hjertet'
  }
];

export default function ForlobPage() {
  const { headerBgColor } = useLogoTheme();
  const [offers, setOffers] = useState(DEFAULT_OFFERS);

  useEffect(() => {
    async function loadForlob() {
      const data = await fetchCollection('forlob');
      if (data && data.length > 0) {
        // Sort by raekkefolge if present
        const sorted = [...data].sort((a, b) => (Number(a.raekkefolge) || 99) - (Number(b.raekkefolge) || 99));
        setOffers(sorted);
      }
    }
    loadForlob();
  }, []);

  return (
    <>
      <SEO
        title="Forløb & Fællesskaber - Lenea Evolinee"
        description="Udforsk Lenea Evolinees forløb for kvinder – gruppeforløbet Forbundet fra Hjertet, personligt 1:1-forløb samt det månedlige fællesskab Lyt – fra Hjertet."
      />

      {/* Hero Header */}
      <section style={{ backgroundColor: headerBgColor }} className="pt-36 pb-20 transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">
            Rejsen hjem til dig selv
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-normal text-[#2E2A27]">
            Forløb & Fællesskaber
          </h1>
          <p className="text-base sm:text-lg text-[#5C524A] leading-relaxed max-w-2xl mx-auto font-sans">
            Trygge, fortrolige rum for kvinder, der længes efter at sænke skuldrene, lytte indad og komme hjem til sig selv.
          </p>
        </div>
      </section>

      {/* Forløb List */}
      <section className="py-20 bg-white border-y border-[#E8DFD5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {offers.map((item, idx) => {
            const isTextFirst = idx % 2 === 1;
            const imgRaw = Array.isArray(item.billede) ? item.billede[0] : item.billede;
            const imgSrc = imgRaw ? imageUrl(imgRaw, 'width=1000,quality=85,format=auto') : null;
            const norm = normalizeSlug(item.forlob_slug);
            let linkHref = item.link;
            if (!linkHref) {
              if (norm === 'forbundet-fra-hjertet') linkHref = '/forbundet-fra-hjertet';
              else if (norm === '1-1-forlob' || norm === '1-1-forloeb') linkHref = '/1-1-forlob';
              else if (norm === 'lyt-fra-hjertet') linkHref = '/lyt-fra-hjertet';
              else if (norm === 'forbundet-med-dit-horoskop' || norm === 'astrologisk-1-1-session' || norm === 'astrologi-fra-hjertet') linkHref = '/forbundet-med-dit-horoskop';
              else linkHref = norm ? `/${norm}` : (item.forlob_slug ? `/${item.forlob_slug}` : '/kontakt');
            }

            return (
              <div
                key={item.id || item.forlob_slug || idx}
                className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center p-8 sm:p-12 rounded-sm bg-[#FAF7F2] border border-[#D8CDBF] shadow-xs"
              >
                {/* Image Block */}
                <div className={`lg:col-span-6 ${isTextFirst ? 'lg:order-2' : 'lg:order-1'}`}>
                  {imgSrc && (
                    <div className="rounded-arch overflow-hidden border border-[#D8CDBF] shadow-md aspect-4/3 bg-[#EAE3D5]">
                      <img
                        src={imgSrc}
                        alt={item.titel}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>

                {/* Text Content Block */}
                <div className={`lg:col-span-6 space-y-6 ${isTextFirst ? 'lg:order-1' : 'lg:order-2'}`}>
                  {item.badge && (
                    <span className="inline-block px-3.5 py-1 rounded-full bg-[#F3E6E2] text-[#864B3D] text-[11px] font-semibold uppercase tracking-widest border border-[#D8ADA0]/30">
                      {item.badge}
                    </span>
                  )}

                  <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
                    {item.titel}
                  </h2>

                  {item.undertitel && (
                    <p className="text-[#98866C] font-medium text-base font-sans">
                      {item.undertitel}
                    </p>
                  )}

                  <p className="text-base text-[#5C524A] leading-relaxed font-sans">
                    {item.kort_beskrivelse ||
                      item.beskrivelse ||
                      (item.lang_beskrivelse
                        ? item.lang_beskrivelse.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 240) + '...'
                        : '')}
                  </p>

                  {item.highlights && (
                    <div className="pt-4 border-t border-[#E8DFD5]">
                      <h4 className="text-xs uppercase tracking-widest font-semibold text-[#2E2A27] mb-2">
                        I dette rum:
                      </h4>
                      <p className="text-xs sm:text-sm text-[#5C524A] leading-relaxed font-sans">
                        {item.highlights}
                      </p>
                    </div>
                  )}

                  <div className="pt-4">
                    <Link
                      to={linkHref}
                      className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#98866C] text-white font-medium text-xs uppercase tracking-widest hover:bg-[#83735C] transition-colors cursor-pointer"
                    >
                      <span>{item.cta_tekst || item.ctaText || 'Læs mere'}</span>
                      <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-[#FAF7F2] text-center border-t border-[#E8DFD5]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">Brug for afklaring?</span>
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27] -mt-2">
            Måske er det nu, du skal vælge dig selv
          </h2>
          <p className="text-base text-[#5C524A] font-sans">
            Hvis du mærker en stille længsel eller en nysgerrighed, er du velkommen til at række ud til en uforpligtende snak om, hvilket forløb der føles rigtigt for dig.
          </p>
          <div>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#98866C] text-white font-medium text-xs uppercase tracking-widest hover:bg-[#83735C] transition-colors cursor-pointer"
            >
              <span>Kontakt Lenea</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
